import { ExpandLess, ExpandMore, TableRowsRounded, ViewModuleRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Container,
  FormControlLabel,
  styled,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { PayloadAction } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from 'redux/store';

import { useAuth } from '../../components/Providers/AuthProvider';
import { getFolderFromProjectId } from '../../redux/actions/folderActions';
import { selectFolders, setCurrentFolder } from '../../redux/reducers/folderReducer';
import { Folder } from '../../Types/folder';
import DebouncedSearchInput from '../DebouncedSearchInput';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useProjects } from '../Providers/ProjectsProvider';
import ProjectsGrid from './ProjectsGrid';
import ProjectsTable from './ProjectsTable';
import { getBooleanFromLocalStorage } from './utils';

const HomePageContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  paddingTop: '50px',
  textAlign: 'left',
  gap: '24px',
});

const ProjectsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '20px',
  justifyContent: 'center',
});

const StyledButton = styled(Button)({
  width: '150px',
  height: '35px',
});

const ExpandableHeader = styled('div')({
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row-reverse',
  '&#customExpandArrow': {
    position: 'absolute',
    top: '0',
    right: '0',
    zIndex: 1,
  },
});

const StyledIcon = styled('div')({
  border: '1px solid black',
});

const ExpandableContentContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  textAlign: 'left',
  gap: '24px',
});

enum ViewMode {
  CARD = 'CARD',
  TABLE = 'TABLE',
}

const HomePageInfo = (): JSX.Element => {
  const {
    setShouldFetch,
    isLoading,
    protectedSetAdminShowAll,
    protectedSetShowArchived,
    adminShowAll,
    showArchived,
    searchQuery,
    setSearchQuery,
    setPage,
  } = useProjects();
  const { pinnedFolders, unpinnedFolders } = useSelector(selectFolders);

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [newVisit, setNewVisit] = useState<boolean>(true);
  const storedHomeContentExpanded = getBooleanFromLocalStorage('isHomeContentExpanded');
  const [isHomeContentExpanded, setIsHomeContentExpanded] = useState(storedHomeContentExpanded);
  const storedViewMode = (localStorage.getItem('HomePageViewMode') as ViewMode) || ViewMode.CARD;
  const [viewMode, setViewMode] = useState<ViewMode>(storedViewMode);
  const { isDealGPTAdmin } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // Make sure we fetch the projects if the user starts on the dealGPT page
  useEffect(() => {
    if (newVisit) {
      setShouldFetch(true);
      setNewVisit(false);
    }
  }, [newVisit, setShouldFetch]);
  const analytics = useAnalytics();

  const handleHomeContentExpandedToggle = useCallback(() => {
    const newState = !isHomeContentExpanded;
    analytics.event(
      AnalyticsEvent.WM_IA_HOME_CONTENT_EXPANDED,
      {
        value: newState.toString(),
      },
      {
        oldHomeContentExpanded: isHomeContentExpanded.toString(),
        newHomeContentExpanded: newState.toString(),
      }
    );
    setIsHomeContentExpanded(newState);
    localStorage.setItem('isHomeContentExpanded', newState.toString());
  }, [analytics, isHomeContentExpanded]);

  const handleCreateProjectClick = async () => {
    analytics.event(AnalyticsEvent.WM_IA_HOME_CREATE_PROJECT);

    navigate('/folder/create');
  };

  const handleProjectClick = useCallback(
    (id: string) => {
      analytics.event(
        AnalyticsEvent.WM_IA_HOME_PROJECT_CLICK,
        {
          id: id,
        },
        {
          projectId: id,
        }
      );

      // loop through pinned folders and unpinned folders to find the folder where projectId === id
      const folder = [...pinnedFolders, ...unpinnedFolders].find(
        (folder) => folder.projectId === id
      );

      if (folder) {
        dispatch(setCurrentFolder(folder));
        navigate(`/folder/${folder.id}`);
      } else {
        dispatch(getFolderFromProjectId(id)).then((action: PayloadAction<Folder | unknown>) => {
          const retFolder = action.payload as Folder;
          if (retFolder && retFolder.id) {
            navigate(`/folder/${retFolder.id}`);
          } else {
            navigate('/intellio-advantage');
          }
        });
      }
    },
    [analytics, navigate, pinnedFolders, unpinnedFolders, dispatch]
  );

  return (
    <div
      style={{
        overflow: 'auto',
        width: '100%',
        maxHeight: 'calc(100vh - var(--banner-height, 0px))',
        height: '100%',
      }}
    >
      <HomePageContainer maxWidth="md">
        <Typography id="dealgpt-welcome" variant="h4" sx={{ fontWeight: '700', fontSize: '36px' }}>
          Welcome to Intellio® Advantage
        </Typography>
        <div>
          <ExpandableHeader id="customExpandArrow" onClick={handleHomeContentExpandedToggle}>
            {isHomeContentExpanded ? (
              <StyledIcon as={ExpandLess} />
            ) : (
              <StyledIcon as={ExpandMore} />
            )}
          </ExpandableHeader>
          <Collapse in={isHomeContentExpanded}>
            <ExpandableContentContainer>
              <Typography variant="subtitle1" sx={{ fontSize: '16px' }}>
                Introducing the Question Library - Now in Intellio Advantage v4.0
              </Typography>
              <Typography>
                Smarter questions, cleaner file views, and personalized project organization.
                Explore the new Question Library, export from Baseball Card View, and keep your home
                page tidy with user-specific archiving.
              </Typography>
              {isDealGPTAdmin && (
                <StyledButton variant="contained" onClick={handleCreateProjectClick}>
                  Create Project
                </StyledButton>
              )}
            </ExpandableContentContainer>
          </Collapse>
        </div>
        <Typography sx={{ fontSize: '24px' }}>My Projects</Typography>
        <DebouncedSearchInput
          value={searchQuery}
          valueUpdated={(value) => {
            analytics.event(
              AnalyticsEvent.WM_IA_HOME_SEARCH,
              {
                value: value,
              },
              {
                searchQuery: value,
              }
            );
            setPage(0);
            setSearchQuery(value);
            setShouldFetch(true);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          shouldFocus={isFocused}
        />
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            minHeight: '40px',
            alignItems: 'center',
            justifyContent: 'center', // Centers the pagination
            position: 'relative', // Allows absolute positioning inside
          }}
        >
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setViewMode(value);
                localStorage.setItem('HomePageViewMode', value); // Store in local storage
              }
            }}
            aria-label="view mode"
            sx={{
              position: 'absolute !important',
              right: 0, // Sticks to the right
            }}
          >
            <ToggleButton value={ViewMode.CARD} sx={{ padding: 1 }}>
              <Tooltip title="Card view" arrow>
                <TableRowsRounded />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={ViewMode.TABLE} sx={{ padding: 1 }}>
              <Tooltip title="Table view" arrow>
                <ViewModuleRounded />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <ProjectsContainer>
          {viewMode === ViewMode.CARD ? (
            <ProjectsGrid handleProjectClick={handleProjectClick} />
          ) : (
            <ProjectsTable handleProjectClick={handleProjectClick} />
          )}
        </ProjectsContainer>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                disabled={isLoading}
                onChange={(e) => {
                  analytics.event(
                    AnalyticsEvent.WM_IA_HOME_SHOW_ARCHIVE,
                    {
                      value: e.target.checked.toString(),
                    },
                    {
                      oldShouldShowArchived: showArchived,
                      newShouldShowArchived: e.target.checked,
                    }
                  );
                  setPage(0);
                  protectedSetShowArchived(e.target.checked);
                }}
                color="primary"
              />
            }
            label="Show archived projects"
          />
        </div>
        {isDealGPTAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '50px' }}>
            <Typography style={{ color: 'grey ' }}>Admin options:</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={adminShowAll}
                  disabled={isLoading}
                  onChange={(e) => {
                    analytics.event(
                      AnalyticsEvent.WM_IA_HOME_SHOW_ALL_PROJECTS,
                      {
                        value: e.target.checked.toString(),
                      },
                      {
                        oldShouldShowAllProjects: adminShowAll,
                        newShouldShowAllProjects: e.target.checked,
                      }
                    );
                    setPage(0);
                    protectedSetAdminShowAll(e.target.checked);
                  }}
                  color="primary"
                />
              }
              label="Show all projects"
            />
          </div>
        )}
      </HomePageContainer>
    </div>
  );
};

export default HomePageInfo;
